import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, LogOut, Mail, Search, X, Film } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, statusColor } from "@/components/admin/status";

const STAT_CARDS = [
  ["New Leads Today", "new_today"], ["New This Week", "new_week"], ["New This Month", "new_month"], ["Strategy Calls", "strategy_calls"],
  ["Website Enquiries", "enquiries"], ["Conversion Rate", "conversion_rate", "%"], ["Uncontacted", "uncontacted"],
  ["Qualified Leads", "qualified"], ["Clients Won", "clients_won"],
];
const INDUSTRIES = ["Manufacturing", "Food & Beverage", "Wholesale", "Packaging", "Construction", "Trades", "Professional Services", "Other"];
const SERVICES = ["Content Production", "Social Media Management", "Paid Advertising", "CRM & AI Systems", "Websites", "Automation", "Not sure yet"];
const BUDGETS = ["Under $2k / month", "$2k – $5k / month", "$5k – $10k / month", "$10k+ / month", "Not sure yet"];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const LeadCentre = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", industry: "", service: "", budget: "", form_type: "" });

  const load = useCallback(async () => {
    const params = { search, ...filters };
    const [s, l, n] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/leads", { params }),
      api.get("/admin/notifications"),
    ]);
    setStats(s.data); setLeads(l.data); setNotifs(n.data);
  }, [search, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => { await api.post("/admin/notifications/read"); setNotifs((ns) => ns.map((n) => ({ ...n, read: true }))); };

  const changeStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    await api.patch(`/admin/leads/${id}`, { status });
    api.get("/admin/stats").then((r) => setStats(r.data));
  };

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/digest/send");
      toast.success(`Digest sent — ${r.data.count} lead${r.data.count === 1 ? "" : "s"} in the last 24h`);
    } catch {
      toast.error("Digest failed to send. Check email settings.");
    } finally { setSendingDigest(false); }
  };
  const clearFilters = () => { setFilters({ status: "", industry: "", service: "", budget: "", form_type: "" }); setSearch(""); };
  const selCls = "rounded-lg border border-[#2E1B16]/15 bg-white px-3 py-2 text-sm text-[#2E1B16] focus:border-[#38BDF8] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body text-[#2E1B16]" data-testid="lead-centre">
      <header className="sticky top-0 z-30 glass-nav border-b border-[#2E1B16]/10">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#38BDF8]" />
            <span className="font-display text-lg tracking-tight">Lead Centre</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/media" data-testid="media-manager-link" className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-full border border-[#2E1B16]/15 text-sm font-semibold hover:border-[#38BDF8] transition-colors"><Film size={15} />Media</Link>
            <button onClick={sendDigest} disabled={sendingDigest} data-testid="send-digest-btn" title="Email me the last 24h of leads now"
              className="hidden sm:flex items-center gap-2 h-10 px-4 rounded-full border border-[#2E1B16]/15 text-sm font-semibold hover:border-[#38BDF8] transition-colors disabled:opacity-50">
              <Mail size={15} />{sendingDigest ? "Sending…" : "Send digest now"}
            </button>
            <div className="relative">
              <button onClick={() => { setShowNotifs((v) => !v); if (!showNotifs && unread) markRead(); }} data-testid="notif-bell" className="relative w-10 h-10 rounded-full border border-[#2E1B16]/15 grid place-items-center hover:border-[#38BDF8] transition-colors">
                <Bell size={17} />
                {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#38BDF8] text-[#2E1B16] text-[10px] font-bold grid place-items-center">{unread}</span>}
              </button>
              {showNotifs && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-2xl border border-[#2E1B16]/10 bg-white panel-shadow p-2" data-testid="notif-panel">
                  {notifs.length === 0 && <p className="text-sm text-[#2E1B16]/50 p-4">No notifications yet.</p>}
                  {notifs.map((n) => (
                    <button key={n.id} onClick={() => { setShowNotifs(false); navigate(`/admin/leads/${n.lead_id}`); }} className="w-full text-left p-3 rounded-xl hover:bg-[#F2EFE9] transition-colors">
                      <p className="text-sm font-semibold">{n.title}</p>
                      <p className="text-xs text-[#2E1B16]/50">{fmtDate(n.at)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="hidden sm:block text-sm text-[#2E1B16]/60">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="w-10 h-10 rounded-full border border-[#2E1B16]/15 grid place-items-center hover:border-rose-400 hover:text-rose-500 transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(260px,1fr)_3fr] gap-3 mb-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-[#2E1B16] text-[#FAF7F2] p-6 flex flex-col justify-between" data-testid="stat-total">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#FAF7F2]/55">Total Leads Submitted</p>
            <div>
              <p className="font-display text-6xl mt-3 leading-none">{stats ? stats.total : "—"}</p>
              <p className="text-xs text-[#FAF7F2]/55 mt-3">
                {stats ? <>{stats.enquiries} enquir{stats.enquiries === 1 ? "y" : "ies"} · {stats.strategy_calls} strategy call{stats.strategy_calls === 1 ? "" : "s"}</> : "Loading…"}
              </p>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {STAT_CARDS.map(([label, key, suffix], i) => (
              <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="rounded-2xl border border-[#2E1B16]/10 bg-white p-4" data-testid={`stat-${key}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2E1B16]/45">{label}</p>
                <p className="font-display text-3xl mt-2 text-[#2E1B16]">{stats ? stats[key] : "—"}{suffix && stats ? suffix : ""}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2E1B16]/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, business, email, phone, website…" data-testid="lead-search"
              className="w-full rounded-lg border border-[#2E1B16]/15 bg-white pl-9 pr-3 py-2 text-sm focus:border-[#38BDF8] focus:outline-none" />
          </div>
          <select value={filters.status} onChange={setF("status")} className={selCls} data-testid="filter-status"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <select value={filters.form_type} onChange={setF("form_type")} className={selCls} data-testid="filter-formtype"><option value="">All types</option><option value="website_enquiry">Enquiry</option><option value="strategy_call">Strategy call</option></select>
          <select value={filters.industry} onChange={setF("industry")} className={selCls} data-testid="filter-industry"><option value="">All industries</option>{INDUSTRIES.map((s) => <option key={s}>{s}</option>)}</select>
          <select value={filters.service} onChange={setF("service")} className={selCls} data-testid="filter-service"><option value="">All services</option>{SERVICES.map((s) => <option key={s}>{s}</option>)}</select>
          <select value={filters.budget} onChange={setF("budget")} className={selCls} data-testid="filter-budget"><option value="">All budgets</option>{BUDGETS.map((s) => <option key={s}>{s}</option>)}</select>
          <button onClick={clearFilters} className="text-sm text-[#2E1B16]/50 hover:text-[#2E1B16] flex items-center gap-1"><X size={14} />Clear</button>
        </div>

        <div className="rounded-2xl border border-[#2E1B16]/10 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lead-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#2E1B16]/45 border-b border-[#2E1B16]/10">
                  {["Name", "Business", "Email", "Phone", "Industry", "Service", "Budget", "Type", "Date", "Status"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`lead-row-${l.id}`} className="border-b border-[#2E1B16]/6 hover:bg-[#F2EFE9]/60 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">{l.full_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.business_name || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.phone || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.industry || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.service || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/70">{l.budget || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><span className="text-xs font-semibold">{l.form_type === "strategy_call" ? "Call" : "Enquiry"}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#2E1B16]/60">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <select value={l.status} onChange={(e) => changeStatus(l.id, e.target.value)} data-testid={`status-select-${l.id}`}
                        className={`text-xs font-semibold rounded-full border px-2.5 py-1 focus:outline-none cursor-pointer ${statusColor(l.status)}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={10} className="px-4 py-12 text-center text-[#2E1B16]/45">No leads match your filters yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeadCentre;
